// RecordDetailService.ts

import { axiosRequest } from "./AxiosService";
import { ApplicationData } from "../pages/RecordDetail/ApplicationData";
import { error } from "console";

/**
 * 申請データ保存処理
 * - applicationEntryIdが無い場合: 新規申請として保存 (/recordDetail/saveNewData)
 * - applicationEntryIdが有る場合: 再申請として保存 (/recordDetail/reApplication)
 *   - 草稿から正式申請への変更
 *   - 既存申請（差し戻し、取り下げ申請）の再申請
 * @param newData 申請データ
 * @param applicationFile 添付ファイル
 * @param imageFiles 備考欄の画像ファイル
 * @param idempotencyKey Idempotency-key(二重送信防止のためのパラメータ)
 */
export const saveNewData = async (
  newData: ApplicationData,
  applicationFile: Record<number, File[]> | null,
  imageFiles?: File[],
  idempotencyKey?: React.RefObject<string | null>,
) => {
  const formData = new FormData();

  formData.append(
    "applicationAggregateResponse",
    new Blob([JSON.stringify(newData)], { type: "application/json" })
  );
  if (applicationFile) {
    const allFiles = Object.values(applicationFile).flat(); // ファイルを一つの配列にまとめる
    allFiles.forEach((file) => {
      formData.append("fileList", file);
    });
  }

  // imageFiles(備考欄に貼り付ける画像を保持)を追加
  if (imageFiles && imageFiles.length > 0) {
    for (const file of imageFiles) {
      formData.append("imageFilesList", file);
    }
  }

  // Idempotency-Keyを設定しているとき、バックエンド側に送信するパラメータに加える
  if (idempotencyKey?.current) {
    formData.append("Idempotency-Key", idempotencyKey.current);
  }
  

  // パラメータの設定
  const options = {
    data: formData,
  };

  if (!newData.applicationEntryId) {
    // サーバー通信処理
    const response: Promise<boolean> = axiosRequest(
      "/recordDetail/saveNewData",
      "POST",
      options
    );
    // 次遷移判定処理（サーバー側から値がtrueで返答される時のみ許可）
    if (response !== undefined) {
      return (await response).valueOf();
    } else {
      return false;
    }
  } else {
    // サーバー通信処理
    const response: Promise<boolean> = axiosRequest(
      "/recordDetail/reApplication",
      "POST",
      options
    );
    // 次遷移判定処理（サーバー側から値がtrueで返答される時のみ許可）
    if (response !== undefined) {
      return (await response).valueOf();
    } else {
      return false;
    }
  }
};

/**
 * 作業報告とファイルを保存する
 * @param submitData - 報告内容と添付ファイル
 * @returns 保存成功時はtrue、失敗時はfalse
 */
export const saveReportsAndFiles = async (
  submitData: ReportAndFiles
): Promise<boolean> => {
  // バリデーション処理を実装する場合下記処理は不要
  // 作業報告の入力内容
  if (!submitData.report && !submitData.fileList) {
    return false;
  }

  const formatData = new FormData();

  formatData.append("userId", submitData.userId ?? "");
  formatData.append("activityId", submitData.activityId ?? "");
  formatData.append("applicationNumber", submitData.applicationNumber ?? "");
  formatData.append("report", submitData.report ?? "");

  submitData.fileList?.forEach((file) => {
    formatData.append("fileList", file);
  });

  const options = {
    data: formatData,
  };

  try {
    // サーバー通信処理
    const response: Promise<boolean> = axiosRequest(
      "/recordDetail/saveReportAndFiles",
      "POST",
      options
    );

    // 次遷移判定処理（サーバー側から値がtrueで返答される時のみ許可）
    if ((await response) !== undefined) {
      return (await response).valueOf();
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * 一時保存データ保存処理
 * - applicationEntryIdの有無に関わらず同じAPIを使用 (/recordDetail/saveDraftData)
 *   - applicationEntryIdが無い場合: 新規草稿として作成
 *   - applicationEntryIdが有る場合: 既存草稿を更新
 * - バックエンド側でapplicationEntryIdの有無により新規作成か更新かを判定
 * @param draftData 一時保存データ
 * @param applicationFile 添付ファイル
 * @param imageFiles 備考欄の画像ファイル
 */
export const saveDraftData = async (
  draftData: ApplicationData,
  applicationFile: Record<number, File[]> | null,
  imageFiles?: File[],
) => {
  const formData = new FormData();

  formData.append(
    "applicationAggregateResponse",
    new Blob([JSON.stringify(draftData)], { type: "application/json" })
  );
  if (applicationFile) {
    const allFiles = Object.values(applicationFile).flat(); // ファイルを一つの配列にまとめる
    allFiles.forEach((file) => {
      formData.append("fileList", file);
    });
  }

  // imageFiles(備考欄に貼り付ける画像を保持)を追加
  if (imageFiles && imageFiles.length > 0) {
    for (const file of imageFiles) {
      formData.append("imageFilesList", file);
    }
  }

  // パラメータの設定
  const options = {
    data: formData,
  };

  // 一時保存は新規・更新に関わらず同じAPIを使用
  // バックエンド側でapplicationEntryIdの有無により新規作成か更新かを判定
  const response: Promise<boolean> = axiosRequest(
    "/recordDetail/saveDraftData",
    "POST",
    options
  );

  // 次遷移判定処理（サーバー側から値がtrueで返答される時のみ許可）
  if (response !== undefined) {
    return (await response).valueOf();
  } else {
    return false;
  }
};

export type ReportAndFiles = {
  userId?: string;
  activityId?: string;
  applicationNumber?: string;
  report?: string;
  fileList?: File[];
};
